import React, { useState } from 'react';
import { Box, ButtonBase, Button, LinearProgress, Tooltip } from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import LogoutIcon from '@mui/icons-material/Logout';
import type { UsageData } from '../../hooks/useUsage';
import { getNavUsage, useAppNavActions } from '../../hooks/useAppNavActions';
import { useScribeNav } from '../../contexts/ScribeNavContext';
import { RECORD_TAB_ID, RecordingDot } from '../../constants/scribeTabs';
import NotificationDropdown from './NotificationDropdown';

export const RAIL_WIDTH_COLLAPSED_PX = 72;
export const RAIL_WIDTH_EXPANDED_PX = 220;
export const RAIL_COLLAPSED_STORAGE_KEY = 'aiScribe.navRailCollapsed';
const RAIL_NAV_ID = 'main-navigation-rail';

// Storage can be blocked (private windows, site data off): the rail then simply starts expanded.
const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean): void => {
  try {
    window.localStorage.setItem(RAIL_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Not remembered; the toggle still works for this visit.
  }
};

interface RailItemProps {
  collapsed: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  secondary?: string;
  tone?: 'default' | 'warning';
  trailing?: React.ReactNode;
  children?: React.ReactNode;
}

const RailItem: React.FC<RailItemProps> = ({
  collapsed,
  icon,
  label,
  onClick,
  active = false,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  secondary,
  tone = 'default',
  trailing,
  children,
}) => (
  <Box sx={{ width: '100%' }}>
    <ButtonBase
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-current={active ? 'page' : undefined}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: collapsed ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0.25 : 1.5,
        px: collapsed ? 0.5 : 1.5,
        py: collapsed ? 1 : 1.1,
        minHeight: 48,
        borderRadius: 'var(--radius-sm)',
        color: tone === 'warning' ? 'var(--warn)' : active ? 'var(--teal)' : 'var(--t2)',
        backgroundColor: active ? 'var(--teal-glow)' : 'transparent',
        transition: 'background-color 0.2s, color 0.2s',
        '&:hover': { backgroundColor: 'var(--teal-dim)', color: 'var(--teal)' },
        '&.Mui-focusVisible': { outline: '2px solid var(--teal)', outlineOffset: -2 },
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', position: 'relative' }} aria-hidden>
        {icon}
      </Box>
      <Box
        component="span"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: collapsed ? 'center' : 'flex-start',
          minWidth: 0,
          textAlign: collapsed ? 'center' : 'left',
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: collapsed ? '0.66rem' : '0.85rem',
            lineHeight: 1.2,
            fontWeight: active ? 600 : 500,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
          {trailing}
        </Box>
        {secondary && !collapsed && (
          <Box
            component="span"
            sx={{
              fontSize: '0.72rem',
              color: 'var(--t3)',
              maxWidth: 130,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {secondary}
          </Box>
        )}
      </Box>
    </ButtonBase>
    {children}
  </Box>
);

interface AppNavRailProps {
  userName: string;
  onSignOut: () => void;
  usage: UsageData | null;
  usageLoading: boolean;
  /** False when the user cannot reach Scribe (trial expired): no tool items, since they would bounce back. */
  showTools?: boolean;
}

/**
 * Fixed left rail for tablet and desktop (sm and up): the Scribe tools at the top, account
 * items at the bottom. 72px collapsed (icon with its label underneath), 220px expanded; the
 * choice is remembered in localStorage.
 */
const AppNavRail: React.FC<AppNavRailProps> = ({ userName, onSignOut, usage, usageLoading, showTools = true }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { visibleTabs, isRecording } = useScribeNav();
  const { activeTool, selectTool, goTo, signOut } = useAppNavActions(onSignOut);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  };

  const { display: usageDisplay, exceeded } = getNavUsage(usage, usageLoading);

  return (
    <Box
      component="nav"
      id={RAIL_NAV_ID}
      aria-label="Main navigation"
      data-collapsed={collapsed}
      sx={{
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        flexShrink: 0,
        boxSizing: 'border-box',
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        width: collapsed ? RAIL_WIDTH_COLLAPSED_PX : RAIL_WIDTH_EXPANDED_PX,
        transition: 'width 0.2s',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1,
        overflowX: 'hidden',
        overflowY: 'auto',
        backgroundColor: 'var(--bg2)',
        borderRight: '1px solid var(--border)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1,
          px: collapsed ? 0 : 1,
          py: 1,
          mb: 1,
          userSelect: 'none',
        }}
      >
        <Box component="img" src="/brand/app-icon.svg" alt="AI-Scribe" sx={{ height: 30, width: 'auto', borderRadius: '4px' }} />
        {!collapsed && (
          <Box
            component="span"
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontSize: '0.85rem',
              fontWeight: 700,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            AI-Scribe
          </Box>
        )}
      </Box>

      {showTools && visibleTabs.map((t) => (
        <RailItem
          key={t.id}
          collapsed={collapsed}
          icon={<t.Icon size={22} strokeWidth={2} />}
          label={t.label}
          active={activeTool === t.id}
          onClick={() => void selectTool(t.id)}
          trailing={t.id === RECORD_TAB_ID && isRecording ? <RecordingDot /> : undefined}
        />
      ))}

      <Box sx={{ flexGrow: 1 }} />

      <NotificationDropdown color="var(--t2)" menuPlacement="right" label="Alerts" stacked={collapsed} />

      {usageDisplay && (
        <Tooltip title={usageDisplay.detail} arrow placement="right" describeChild>
          <Box>
            <RailItem
              collapsed={collapsed}
              icon={<DataUsageIcon />}
              label="Usage"
              ariaLabel={`Usage: ${usageDisplay.detail}`}
              onClick={() => void goTo('/subscription')}
            >
              {!collapsed && (
                <Box sx={{ px: 1.5, pb: 0.75 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usageDisplay.percent, 100)}
                    color={usageDisplay.barColor}
                    sx={{ height: 6, borderRadius: 3, backgroundColor: 'var(--s2)' }}
                  />
                  <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem', color: 'var(--t3)' }}>
                    {usageDisplay.text}
                  </Box>
                </Box>
              )}
            </RailItem>
          </Box>
        </Tooltip>
      )}

      {exceeded && (
        <Button
          variant="contained"
          size="small"
          color="warning"
          onClick={() => void goTo('/pricing')}
          sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, minWidth: 0 }}
        >
          Upgrade
        </Button>
      )}

      <RailItem
        collapsed={collapsed}
        icon={<AccountCircleOutlinedIcon />}
        label="Account"
        secondary={userName}
        ariaLabel={`Account and subscription, signed in as ${userName}`}
        onClick={() => void goTo('/subscription')}
      />
      <RailItem collapsed={collapsed} icon={<LogoutIcon />} label="Sign out" onClick={() => void signOut()} />

      <Box sx={{ borderTop: '1px solid var(--border)', mt: 0.5, pt: 0.5 }}>
        <RailItem
          collapsed={collapsed}
          icon={collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          label="Navigation"
          ariaLabel="Navigation"
          ariaExpanded={!collapsed}
          ariaControls={RAIL_NAV_ID}
          onClick={toggle}
        />
      </Box>
    </Box>
  );
};

export default AppNavRail;
